import React from "react";
import { AlertCircle, Clock, Crown } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";

const SubscriptionWarnningModal = ({
  isOpen = true,
  onClose = () => { },
  planType = "Free Plan",
  planName = "Free Plan",
  webEndDate = null,
  subscriptionStartDate = null,
}) => {
  const navigate = useNavigate();

  // ✅ Remaining days calculate karo
  // ✅ End date ki 11:59:59 PM tak valid
const getEndDateTime = () => {
    if (!webEndDate) return null;
    const end = new Date(webEndDate);
    end.setHours(23, 59, 59, 999); // 11:59:59.999 PM
    return end;
};

const getRemainingDays = () => {
    const end = getEndDateTime();
    if (!end) return 0;
    const today = new Date();
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : 0;
};


  // ✅ Subscription duration calculate karo (total days)
  const getTotalDays = () => {
    if (!subscriptionStartDate || !webEndDate) return null;
    const start = new Date(subscriptionStartDate);
    const end = new Date(webEndDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  // ✅ End date formatted
  const getFormattedEndDate = () => {
    if (!webEndDate) return "N/A";
    return new Date(webEndDate).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const remainingDays = getRemainingDays();
  const totalDays = getTotalDays();
 const isExpired = new Date() > getEndDateTime();
  const isFreePlan = planType === "Free Plan";

  // ✅ Dynamic title & message
  const getTitle = () => {
    if (isExpired) return `${planName} Ended — Upgrade to Continue`;
    return `Your ${planName} is Expiring Soon`;
  };

  const getSubMessage = () => {
    if (isExpired)
      return `Your <b>${planName}</b> plan expired on <b>${getFormattedEndDate()}</b>. Upgrade now to retain access and continue using all features.`;
    return `Your <b>${planName}</b> plan expires on <b>${getFormattedEndDate()}</b> — only <b>${remainingDays} day${remainingDays !== 1 ? "s" : ""}</b> remaining. Upgrade now to avoid any disruption.`;
  };

  const getAlertMessage = () => {
    if (isExpired) return "Don't lose access — Secure your data by upgrading now";
    return `Hurry! Only ${remainingDays} day${remainingDays !== 1 ? "s" : ""} left — Upgrade before your plan expires`;
  };

  return (
    <div
      className={`modal fade ${isOpen ? "show d-block" : ""}`}
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
        style={{ maxWidth: "600px" }}
      >
        <div
          className="modal-content border-0 shadow-lg"
          style={{ borderRadius: "18px", overflow: "hidden" }}
        >
          {/* HEADER ICON */}
          <div className="text-center mt-4">
            <div
              className="d-flex align-items-center justify-content-center mx-auto rounded-circle shadow"
              style={{
                width: "75px",
                height: "75px",
                background: isExpired
                  ? "linear-gradient(135deg, #ffcccc, #ffe5e5)"
                  : "linear-gradient(135deg, #fff3cd, #fff8e1)",
                boxShadow: isExpired
                  ? "0 6px 16px rgba(255, 0, 0, 0.3)"
                  : "0 6px 16px rgba(255, 193, 7, 0.4)",
              }}
            >
              {isExpired ? (
                <AlertCircle size={38} className="text-danger" />
              ) : (
                <Clock size={38} className="text-warning" />
              )}
            </div>
          </div>

          {/* PLAN BADGE */}
          <div className="text-center mt-3">
            <span
              className="badge px-3 py-2"
              style={{
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                color: "#fff",
                fontSize: "13px",
                borderRadius: "20px",
              }}
            >
              <Crown size={13} className="me-1" />
              {planName}
              {totalDays ? ` — ${totalDays} Day Plan` : ""}
            </span>
          </div>

          {/* TITLE & DESCRIPTION */}
          <div className="modal-body text-center px-4 pb-2">
            <h4 className="fw-bold mt-3" style={{ fontSize: "20px", color: "#222" }}>
              {getTitle()}
            </h4>

            <p
              className="text-muted mt-2 mb-3"
              style={{ fontSize: "15px" }}
              dangerouslySetInnerHTML={{ __html: getSubMessage() }}
            />

            {/* REMAINING DAYS BADGE */}
            {!isExpired && (
              <div
                className="d-inline-block px-4 py-2 mb-3 rounded-pill fw-bold"
                style={{
                  background: remainingDays <= 3 ? "#ffe5e5" : "#e8f5e9",
                  color: remainingDays <= 3 ? "#c0392b" : "#2e7d32",
                  fontSize: "16px",
                  border: `1.5px solid ${remainingDays <= 3 ? "#f5c6cb" : "#a5d6a7"}`,
                }}
              >
                ⏳ {remainingDays} Day{remainingDays !== 1 ? "s" : ""} Remaining
              </div>
            )}

            <div className="alert alert-warning d-flex align-items-center justify-content-center gap-2 py-2">
              <AlertCircle size={18} />
              <span className="fw-semibold">{getAlertMessage()}</span>
            </div>

            {/* OPTIONS */}
            <div className="text-start mt-4 px-2">
              <div className="mb-3">
                <span className="fw-semibold text-dark">• Upgrade to a Paid Plan (Recommended):</span>
                <p className="text-muted mb-1" style={{ fontSize: "14px" }}>
                  Unlock all features, maintain data access, and grow your business without limitations. Choose the best plan from our{" "}
                  <a onClick={() => {
                    navigate("/subscription");
                    onClose();
                  }} className="text-primary fw-bold text-decoration-none">
                    Pricing Page
                  </a>.
                </p>
              </div>

              {isFreePlan && (
                <div className="mb-3">
                  <span className="fw-semibold text-dark">• Continue with Free Plan:</span>
                  <p className="text-muted mb-1" style={{ fontSize: "14px" }}>
                    Access basic features only. Suitable for personal use, but not ideal for business growth.
                  </p>
                </div>
              )}

              {!isExpired && (
                <div className="mb-2">
                  <span className="fw-semibold text-dark">• Need More Time?</span>
                  <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    You can{" "}
                    <span
                      className="text-primary fw-bold"
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => {
                        navigate("/subscription");
                        onClose();
                      }}
                    >
                      extend your plan
                    </span>
                    , or upgrade now to avoid feature restrictions.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* BUTTONS */}
          <div className="modal-footer d-flex justify-content-center border-0 pb-4 pt-2 gap-2">
            <button
              onClick={() => {
                navigate("/subscription");
                onClose();
              }}
              className="btn btn-primary fw-bold px-4 py-2"
              style={{
                borderRadius: "8px",
                minWidth: "180px",
                boxShadow: "0 4px 12px rgba(0, 123, 255, 0.5)",
              }}
            >
              Choose & Upgrade Plan
            </button>

            {/* Close button sirf tab dikhao jab expired na ho */}
            {!isExpired && (
              <button
                onClick={onClose}
                className="btn btn-outline-secondary fw-bold px-4 py-2"
                style={{ borderRadius: "8px", minWidth: "140px" }}
              >
                Remind Me Later
              </button>
            )}
          </div>

          {/* X CLOSE */}
          <button
            type="button"
            className="btn-close position-absolute"
            style={{ top: "15px", right: "15px" }}
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
};

export default SubscriptionWarnningModal;